import { useCallback, useMemo } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import TruncatedText from 'src/components/TruncatedText';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import TableCard from 'src/components/Table/TableCard';
import { DetailCard } from 'src/components/summary/DetailCard';
import { SummaryCardSkeleton } from 'src/components/summary/SummaryCardSkeleton';
import { SummaryGrid } from 'src/components/summary/SummaryGrid';
import { SummaryValue } from 'src/components/summary/SummaryValue';
import {
  useDeviceUnmatchedFileDetails,
  useInfiniteDeviceUnmatchedFileEvents,
} from 'src/hooks/device';
import { formatDate } from 'src/utils/formatTime';

const compactCellSx = {
  fontSize: '0.7rem',
  py: 0.75,
  lineHeight: 1.3,
};

const compactHeaderSx = {
  fontSize: '0.7rem',
  fontWeight: 600,
  py: 0.75,
};

const DeviceUnmatchedFileDetailsModal = ({ open, onClose, deviceId, fileId, fallbackRow }) => {
  const detailsQuery = useDeviceUnmatchedFileDetails({ deviceId, fileId, enabled: open });
  const eventsQuery = useInfiniteDeviceUnmatchedFileEvents({ deviceId, fileId, enabled: open });
  const details = detailsQuery.data;
  const title =
    details?.fileName ||
    fallbackRow?.fileName ||
    details?.sha256 ||
    fallbackRow?.sha256 ||
    'Unmatched File';

  const events = useMemo(
    () => eventsQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [eventsQuery.data],
  );

  const loadMoreEvents = useCallback(() => {
    if (eventsQuery.hasNextPage) {
      eventsQuery.fetchNextPage();
    }
  }, [eventsQuery]);

  const [eventsSentryRef] = useInfiniteScroll({
    loading: eventsQuery.isFetchingNextPage,
    hasNextPage: eventsQuery.hasNextPage,
    disabled: !open,
    onLoadMore: loadMoreEvents,
    rootMargin: '0px 0px 240px 0px',
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#3f3f3f' } }}>
      <DialogTitle sx={{ pr: 5 }}>
        {title}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12, color: '#CFCFCF' }}>
          <Iconify icon="mdi:close" width={20} height={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {detailsQuery.isLoading ? (
            <SummaryCardSkeleton rows={2} />
          ) : (
            <DetailCard compact>
              <Stack spacing={1.25}>
                <Typography variant="subtitle2">File Summary</Typography>
                <SummaryValue compact label="SHA-256 Hash">
                  {details?.sha256 || fallbackRow?.sha256}
                </SummaryValue>
                <SummaryValue compact label="Authenticode SHA-256 Hash">
                  {details?.authenticodeHashSha256}
                </SummaryValue>
                <SummaryGrid compact>
                  <SummaryValue compact label="Original File Name">
                    {details?.originalFileName}
                  </SummaryValue>
                  <SummaryValue compact label="Product Name">
                    {details?.productName}
                  </SummaryValue>
                  <SummaryValue compact truncate label="File Version">
                    {details?.fileVersion}
                  </SummaryValue>
                </SummaryGrid>
                <SummaryGrid compact>
                  <SummaryValue compact label="Last Seen">
                    {details?.lastSeenDate ? formatDate(details.lastSeenDate, 'yyyy-MM-dd') : '--'}
                  </SummaryValue>
                  <SummaryValue compact label="Last 30 Event Count">
                    {details?.last30Count ?? 0}
                  </SummaryValue>
                  <SummaryValue compact label="Last 90 Event Count">
                    {details?.last90Count ?? 0}
                  </SummaryValue>
                </SummaryGrid>
              </Stack>
            </DetailCard>
          )}

          <TableCard title="Recent Events" maxHeight={320} edgePadding={3}>
            <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead sx={{ backgroundColor: '#3f3f3f' }}>
                <TableRow>
                  <TableCell sx={{ ...compactHeaderSx, width: '13%' }}>Date</TableCell>
                  <TableCell sx={{ ...compactHeaderSx, width: '25%' }}>File Name</TableCell>
                  <TableCell sx={{ ...compactHeaderSx, width: '24%' }}>Launching Process</TableCell>
                  <TableCell sx={{ ...compactHeaderSx, width: 'auto' }}>File Path</TableCell>
                  <TableCell sx={{ ...compactHeaderSx, width: 96, whiteSpace: 'nowrap' }}>
                    Event Count
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
                {eventsQuery.isLoading ? (
                  <LoadingRows columnCount={5} rowCount={4} />
                ) : (
                  <>
                    {events.map((event, index) => (
                      <TableRow
                        key={`${event.eventSystemTime}-${event.fullFilePath}-${index}`}
                        ref={index === events.length - 1 ? eventsSentryRef : null}>
                        <TableCell sx={compactCellSx}>
                          <TruncatedText
                            value={
                              event.eventSystemTime
                                ? formatDate(event.eventSystemTime, 'yyyy-MM-dd')
                                : ''
                            }
                          />
                        </TableCell>
                        <TableCell sx={compactCellSx}>
                          <TruncatedText value={event.fileName} />
                        </TableCell>
                        <TableCell sx={compactCellSx}>
                          <TruncatedText value={event.processName} muted fontSize="0.6rem" />
                        </TableCell>
                        <TableCell sx={compactCellSx}>
                          <TruncatedText value={event.fullFilePath} muted fontSize="0.6rem" />
                        </TableCell>
                        <TableCell
                          sx={{ ...compactCellSx, whiteSpace: 'nowrap', color: '#9CA3AF' }}>
                          {event.deviceWdacEventsCount ?? 0}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!eventsQuery.isLoading && eventsQuery.isFetchingNextPage && (
                      <LoadingRows columnCount={5} rowCount={2} />
                    )}
                    {!eventsQuery.isFetching && !events.length && (
                      <NoDataCell colSpan={5}>No events found.</NoDataCell>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableCard>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default DeviceUnmatchedFileDetailsModal;
